// ============================================
// @gs/ui — DataTable générique
// Design System GS v1.0 — slate palette
// ============================================

import React from 'react';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  /** Appliqué sur `td` ; aussi sur `th` sauf si `headerClassName` est défini */
  className?: string;
  /** Surcharge des classes du `th` pour cette colonne */
  headerClassName?: string;
  sortable?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T) => string;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
}

export function DataTable<T>({
  columns,
  data,
  rowKey,
  emptyMessage = 'Aucune donnée',
  onRowClick,
  isLoading,
}: DataTableProps<T>) {
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="py-12 text-center text-[14px] text-slate-400 dark:text-slate-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[14px]">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-700/60">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`py-2.5 px-4 text-left text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap
                            text-slate-600 dark:text-slate-300
                            ${col.headerClassName ?? col.className ?? ''}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={() => onRowClick?.(row)}
              className={`border-b border-slate-100 dark:border-slate-700/50 transition-colors ${
                onRowClick
                  ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40'
                  : ''
              }`}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`py-3 px-4 text-[14px] text-slate-700 dark:text-slate-200 ${col.className ?? ''}`}
                >
                  {col.render
                    ? col.render(row)
                    : (row as Record<string, unknown>)[col.key] as React.ReactNode}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
